import React, { useState } from "react";
import config from "../../../config";
import TextArea from "../../TextArea/TextArea";
import "../Modal2.css";
import { authFetch } from "../../../utils/Security/authFetch";
import FileUploader from "../../FileUpload/FileUpload";

type JournalModalProps = {
  data: {
    ticketId: string;
    incidentCloseDatetime?: string;
    errorType?: string;
    closureCode?: string;
    solution?: string;
    filepath?: string[];
  };
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  isPauseJournal?: boolean; // ✅ add this

  journals?: {
    username: string;
    message: string;
    date: string;
    filepath?: string[];
  }[];
};

const JournalModal: React.FC<JournalModalProps> = ({
  data,
  title,
  onConfirm,
  onCancel,
  journals = [],
  isPauseJournal = false, // ✅ add this
}) => {
  const [solution, setSolution] = useState<string>(data.solution || "");
  const [filepath, setFilePath] = useState<string[]>(data.filepath || []);

  const postMessage = async (payload: any) => {
    const res = await authFetch(`${config.apiBaseUrl}/api/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.text();
      throw new Error(err || "Failed to post message");
    }
    return res;
  };

  const handleConfirm = async () => {
    if (!solution.trim()) {
      alert("Please enter a journal note before submitting.");
      return;
    }

    const username = localStorage.getItem("username") || "System";

    let message = `ID : ${data.ticketId} ${isPauseJournal ? "Paused " : ""} With Comment :${solution}`;
    if (filepath.length > 0) {
      message += " | Files: " + filepath.join(", ");
    }

    const payload: any = {
      username,
      message,
      date: new Date().toISOString(),
      read: false,
      ticketId: data.ticketId,
    };

    if (filepath.length > 0) payload.filepath = filepath;

    localStorage.setItem("comment", solution);

    try {
      await postMessage(payload);
      alert("Journal Added Successfully!");
      onConfirm();
    } catch (error) {
      alert(`Failed to Add Journal: ${(error as Error).message}`);
    }
  };

  const downloadFile = (path: string) => {
    const fileName = path.split(/[/\\]/).pop() || "file";
    const url = `${config.apiBaseUrl}/api/incidents/files/${fileName}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="modal-overlay"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        padding: "1rem",
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "#fff",
          padding: "2rem 2.5rem",
          borderRadius: "12px",
          maxWidth: 600,
          width: "100%",
          boxShadow:
            "0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontWeight: 600,
            fontSize: "1.5rem",
            color: "#333",
          }}
        >
          {title || "Add Journal"}
        </h2>

        <TextArea
          label="Journal"
          onChange={setSolution}
          style={{ width: "100%", height: "150px" }}
          value={solution}
        />

        <FileUploader
          value={filepath}
          onRemove={(path) =>
            setFilePath((prev) => prev.filter((p) => p !== path))
          }
          onChange={async (files, paths) => {
            if (!files || !paths.length) return;

            const uploadedPaths: string[] = [];

            for (let i = 0; i < files.length; i++) {
              const safeFileName = files[i].name.replace(/\s+/g, "_");
              const renamedFile = new File([files[i]], safeFileName, {
                type: files[i].type,
              });
              const formData = new FormData();
              formData.append("file", renamedFile);

              try {
                const response = await authFetch(
                  `${config.apiBaseUrl}/api/incidents/upload`,
                  { method: "POST", body: formData },
                );
                if (response.ok) {
                  let { path } = await response.json();
                  path = path.replace(/\s+/g, "_");
                  uploadedPaths.push(path);
                } else {
                  console.error(`Upload failed for file: ${safeFileName}`);
                }
              } catch (error) {
                console.error("Error uploading file", error);
              }
            }

            if (uploadedPaths.length) {
              setFilePath((prev) => [...prev, ...uploadedPaths]);
            }
          }}
        />

        {filepath.length > 0 && (
          <div>
            <h4 style={{ marginBottom: "0.5rem", color: "#444" }}>
              Uploaded Files:
            </h4>
            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
              {filepath.map((file, index) => (
                <li key={index}>
                  <button
                    style={{
                      color: "#f97316",
                      textDecoration: "underline",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    onClick={() => downloadFile(file)}
                  >
                    {file.split("/").pop()}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Display Existing Journals */}
        {journals.length > 0 && (
          <div>
            <h3 style={{ marginBottom: "0.5rem", color: "#333" }}>
              Previous Journals
            </h3>
            {journals.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  marginBottom: "10px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div style={{ marginBottom: "5px", fontWeight: 600 }}>
                  {entry.username}: {entry.message.split(" | Files:")[0]}
                </div>

                {entry.filepath && entry.filepath.length > 0 && (
                  <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                    {entry.filepath.map((path, i) => {
                      const normalizedPath = path.replace(/\\/g, "/");
                      const fileName =
                        normalizedPath.split("/").pop() || `File${i + 1}`;
                      const ext = fileName.split(".").pop()?.toLowerCase();
                      const isImage = [
                        "png",
                        "jpg",
                        "jpeg",
                        "gif",
                        "bmp",
                        "webp",
                      ].includes(ext || "");
                      const fileUrl = `${config.apiBaseUrl}/api/incidents/files/${fileName}`;

                      return (
                        <li key={i} style={{ marginBottom: "5px" }}>
                          {isImage ? (
                            <img
                              src={fileUrl}
                              alt={fileName}
                              style={{ maxWidth: "200px", borderRadius: "5px" }}
                            />
                          ) : (
                            <button
                              style={{
                                color: "blue",
                                textDecoration: "underline",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                              }}
                              onClick={() => downloadFile(path)}
                            >
                              {fileName}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div style={{ fontSize: "0.8rem", color: "#555" }}>
                  {new Date(entry.date).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: "0.5rem 1.2rem",
              borderRadius: "6px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              color: "#444",
              cursor: "pointer",
              fontWeight: 500,
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f0f0f0")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#fff")
            }
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            style={{
              padding: "0.5rem 1.5rem",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#f97316",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
              boxShadow: "0 4px 8px rgba(249, 115, 22, 0.4)",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#c4510b")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#f97316")
            }
          >
            Add Journal
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalModal;
